/**
 * Presence roster store — an external store (useSyncExternalStore source) holding the live
 * roster per record reference. Kept outside React because presence is high-frequency,
 * selectively-consumed state that must not live in Context (a heartbeat must not re-render
 * every subscriber).
 *
 * Authority model: this is the client's eventually-consistent view of who is present,
 * assembled from relayed `PresenceMemberActive` / `PresenceMemberLeft` events plus a TTL
 * sweep. It is never authoritative server membership (Azure SignalR exposes none); it
 * self-heals via heartbeat-refreshed `lastSeen` timestamps and `pruneExpired`.
 *
 * Snapshot stability: `getRoom` returns a cached, referentially-stable array per room and
 * only recomputes when that room actually changes, so `useSyncExternalStore` consumers do
 * not re-render on unrelated rooms or on heartbeats that do not change membership.
 */

import { presenceRefKey, type PresenceEntityType, type PresenceMember } from "./presenceWire";

const EMPTY_ROOM: readonly PresenceMember[] = Object.freeze([]);

interface RoomEntry {
    member: PresenceMember;
    lastSeenMs: number;
}

export interface PresenceStore {
    subscribe(listener: () => void): () => void;
    /** Referentially-stable roster for a record, sorted deterministically. */
    getRoom(entityType: PresenceEntityType, entityId: string): readonly PresenceMember[];
    /** Upsert a viewer as active (join / heartbeat / sync reply). */
    upsertActive(
        entityType: PresenceEntityType,
        entityId: string,
        member: PresenceMember,
        nowMs: number,
    ): void;
    /** Remove a single viewer token (explicit leave / disconnect). */
    remove(entityType: PresenceEntityType, entityId: string, connectionId: string): void;
    /** Evict viewers whose last heartbeat is older than the TTL. */
    pruneExpired(nowMs: number, ttlMs: number): void;
    /** Drop the entire roster (used on reconnect — server state is re-announced fresh). */
    clear(): void;
}

function sameDisplay(a: PresenceMember, b: PresenceMember): boolean {
    return (
        a.galileoUserId === b.galileoUserId &&
        a.displayName === b.displayName &&
        a.initials === b.initials &&
        a.shortLabel === b.shortLabel
    );
}

/**
 * Deterministic roster order, without mutating the input. Name first, then
 * the stable user id, then the connection id, so the same people keep the
 * same order across reconnects and the snapshot diff stays minimal.
 */
export function sortMembers(members: readonly PresenceMember[]): PresenceMember[] {
    return [...members].sort((a, b) => {
        const byName = a.displayName.localeCompare(b.displayName);
        if (byName !== 0) return byName;
        const byUser = a.galileoUserId.localeCompare(b.galileoUserId);
        return byUser !== 0 ? byUser : a.connectionId.localeCompare(b.connectionId);
    });
}

export function createPresenceStore(): PresenceStore {
    const rooms = new Map<string, Map<string, RoomEntry>>();
    const snapshotCache = new Map<string, readonly PresenceMember[]>();
    const listeners = new Set<() => void>();

    function notify(): void {
        for (const listener of listeners) {
            listener();
        }
    }

    function invalidate(key: string): void {
        snapshotCache.delete(key);
    }

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },

        getRoom(entityType, entityId) {
            const key = presenceRefKey(entityType, entityId);
            const cached = snapshotCache.get(key);
            if (cached !== undefined) {
                return cached;
            }
            const room = rooms.get(key);
            if (room === undefined || room.size === 0) {
                return EMPTY_ROOM;
            }
            const snapshot = sortMembers([...room.values()].map((entry) => entry.member));
            snapshotCache.set(key, snapshot);
            return snapshot;
        },

        upsertActive(entityType, entityId, member, nowMs) {
            const key = presenceRefKey(entityType, entityId);
            let room = rooms.get(key);
            if (room === undefined) {
                room = new Map<string, RoomEntry>();
                rooms.set(key, room);
            }
            const existing = room.get(member.connectionId);
            const membershipChanged =
                existing === undefined || !sameDisplay(existing.member, member);
            room.set(member.connectionId, { member, lastSeenMs: nowMs });

            // A pure heartbeat (same connection, unchanged display) only refreshes lastSeen — it
            // must not invalidate the snapshot or notify, or every beat would re-render every
            // consumer.
            if (membershipChanged) {
                invalidate(key);
                notify();
            }
        },

        remove(entityType, entityId, connectionId) {
            const key = presenceRefKey(entityType, entityId);
            const room = rooms.get(key);
            if (room === undefined) {
                return;
            }
            if (room.delete(connectionId)) {
                if (room.size === 0) {
                    rooms.delete(key);
                }
                invalidate(key);
                notify();
            }
        },

        pruneExpired(nowMs, ttlMs) {
            let changed = false;
            for (const [key, room] of rooms) {
                let roomChanged = false;
                for (const [connectionId, entry] of room) {
                    if (nowMs - entry.lastSeenMs > ttlMs) {
                        room.delete(connectionId);
                        roomChanged = true;
                    }
                }
                if (roomChanged) {
                    if (room.size === 0) {
                        rooms.delete(key);
                    }
                    invalidate(key);
                    changed = true;
                }
            }
            if (changed) {
                notify();
            }
        },

        clear() {
            if (rooms.size === 0) {
                return;
            }
            rooms.clear();
            snapshotCache.clear();
            notify();
        },
    };
}

/** Process-wide presence roster. The shell-mounted channel feeds it; record hooks read it. */
export const presenceStore = createPresenceStore();
